/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { LocateFixed, User } from 'lucide-react';
import './SearchBar.css';
import { SEARCH_SUGGESTIONS_Z_INDEX } from '../../styles/layering';
import { fetchAutocomplete, AutocompleteItem } from '../../services/autocompleteService';
import { CircularProgress } from '@mui/material';
import { findMatchingProfessionsSimple } from '../../utils/DefaultProfessions';
import {
  isBookingAskAvailable,
  labelForProfessionSlug,
} from '../../utils/prosDiscoverySearch';
import { submitDiscoveryOrAsk } from '../../utils/submitDiscoveryOrAsk';
import { useGoogleMaps } from '../../contexts/GoogleMapsLoaderContext';

import SearchBarOuField from './SearchBarOuField';
import { FeatureFlag, useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { useGeoCountry } from '../../hooks/useGeoCountry';
import { placesSearchCountryRestriction } from '../../config/geoLocale';
import { useTranslation } from 'react-i18next';
import {
  GeolocationRequestError,
  getCurrentCoordinates,
  reverseGeocodeCoords,
} from '../../utils/geolocationUtils';
import { readGoogleLatLng } from '../../utils/parseGooglePlaceAddress';

function coordsFromGoogleLocation(
  loc: google.maps.LatLng | google.maps.LatLngLiteral | undefined | null
): { lat: number; lon: number } | null {
  const { lat, lng } = readGoogleLatLng(loc as Parameters<typeof readGoogleLatLng>[0]);
  if (lat == null || lng == null) return null;
  if (lat === 0 && lng === 0) return null;
  return { lat, lon: lng };
}

function resolveGooglePlaceCoords(
  placeId: string,
  sessionToken: google.maps.places.AutocompleteSessionToken | null
): Promise<{ lat: number; lon: number; address: string } | null> {
  return new Promise(resolve => {
    const places = window.google?.maps?.places;
    if (!places || !placeId) {
      resolve(null);
      return;
    }
    const svc = new places.PlacesService(document.createElement('div'));
    svc.getDetails(
      {
        placeId,
        fields: ['geometry', 'formatted_address', 'name'],
        sessionToken: sessionToken ?? undefined,
      },
      (place, status) => {
        if (status !== places.PlacesServiceStatus.OK || !place) {
          resolve(null);
          return;
        }
        const coords = coordsFromGoogleLocation(place.geometry?.location);
        if (!coords) {
          resolve(null);
          return;
        }
        resolve({
          ...coords,
          address: place.formatted_address || place.name || '',
        });
      }
    );
  });
}

function SuggestionAvatar({ src }: { src?: string | null }) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const showImg = Boolean(src) && src !== failedSrc;

  return (
    <span
      className={`suggestion-avatar${showImg ? '' : ' suggestion-avatar--placeholder'}`}
      aria-hidden
    >
      {showImg ? (
        <img src={src!} alt='' onError={() => setFailedSrc(src!)} />
      ) : (
        <User size={16} strokeWidth={2} />
      )}
    </span>
  );
}

// Hooks intégrés temporairement pour le build
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = React.useState<T>(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// Cache simplifié intégré
const SimpleCache = {
  getAutocompleteResult: (query: string) => {
    try {
      const cached = localStorage.getItem(`autocomplete_cache_${query.toLowerCase()}`);
      if (cached) {
        const data = JSON.parse(cached);
        if (Date.now() - data.timestamp < 300000) {
          // 5 minutes
          return data.results;
        }
      }
    } catch (error) {
      // Ignore errors
    }
    return null;
  },

  setAutocompleteResult: (query: string, results: any[]) => {
    try {
      const data = { results, timestamp: Date.now() };
      localStorage.setItem(`autocomplete_cache_${query.toLowerCase()}`, JSON.stringify(data));
    } catch (error) {
      // Ignore errors
    }
  },

  cleanExpiredEntries: () => {
    // Simple cleanup - can be enhanced later
  },
};

// Professions par défaut intégrées - SYNCHRONISÉES AVEC LA BASE DE DONNÉES
// SUPPRIMÉ: DEFAULT_PROFESSIONS_SIMPLE - maintenant géré par DefaultProfessions.ts

// Supprimé : utilise maintenant la fonction du module DefaultProfessions
// function findMatchingProfessionsSimple(query: string): any[] {
//   if (!query.trim()) return DEFAULT_PROFESSIONS_SIMPLE;

//   const normalizedQuery = query.toLowerCase();
//   const synonyms: Record<string, string> = {
//     'veto': 'vétérinaire',
//     'dresseur': 'éducateur canin',
//     'petsitter': 'pension canine',
//     'massage': 'masseur',
//     'toilettage': 'toiletteur'
//   };

//   const expandedQuery = synonyms[normalizedQuery] || query;

//   const matches = DEFAULT_PROFESSIONS_SIMPLE.filter(prof =>
//     prof.name.toLowerCase().includes(normalizedQuery) ||
//     prof.name.toLowerCase().includes(expandedQuery.toLowerCase())
//   );

//   return matches.length > 0 ? matches : DEFAULT_PROFESSIONS_SIMPLE;
// }

interface SearchBarProps {
  onSearch?: () => void;
  withMarginTop?: boolean;
  /** Texte d’aide au-dessus du formulaire (désactiver dans la navbar) */
  showGuidance?: boolean;
  /** Métier pré-rempli dans le champ « Je cherche » si aucun param URL (MetierLanding) */
  initialProfession?: string;
  /** Ville pré-remplie dans le champ « Où » si aucun param URL (MetierCityLanding) */
  initialAddress?: string;
  /** Formulaire seul, utilisable dans une conversation (hero). */
  embedded?: boolean;
  /** Préfixe d’id des champs (évite les doublons navbar + hero). */
  idPrefix?: string;
}
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string;
const LIBRARIES: 'places'[] = ['places'];

// Fonction utilitaire pour la distance de Levenshtein
function levenshtein(a: string, b: string): number {
  const matrix = Array.from({ length: a.length + 1 }, (_, i) => [i]);
  for (let j = 1; j <= b.length; j++) matrix[0][j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      if (a[i - 1] === b[j - 1]) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1, // suppression
          matrix[i][j - 1] + 1, // insertion
          matrix[i - 1][j - 1] + 1 // substitution
        );
      }
    }
  }
  return matrix[a.length][b.length];
}

// SUPPRIMÉ: Ancien dictionnaire de synonymes - maintenant géré par DefaultProfessions.ts
// SUPPRIMÉ: function expandWithSynonyms - remplacé par findMatchingProfessionsSimple

// Version simple du fuzzy match pour les synonymes (moins strict)
function fuzzyMatchSimple(input: string, target: string): boolean {
  const normalize = (str: string) =>
    str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '');

  const inputNorm = normalize(input);
  const targetNorm = normalize(target);

  return targetNorm.includes(inputNorm) || inputNorm.includes(targetNorm);
}

// Fonction de matching intelligent
function smartMatch(input: string, target: string): boolean {
  // Normalise : enlève accents, casse, espaces
  const normalize = (str: string) =>
    str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '');

  const inputNorm = normalize(input);
  const targetNorm = normalize(target);

  // Recherche partielle + distance de Levenshtein
  return targetNorm.includes(inputNorm) || levenshtein(inputNorm, targetNorm) <= 2;
}

// Fonction de matching fuzzy pour les métiers (simplifiée)
function fuzzyMatch(input: string, target: string): boolean {
  if (!input.trim()) return false;

  // Utiliser le système de correspondance de DefaultProfessions
  const matchingProfessions = findMatchingProfessionsSimple(input);

  // Vérifier si le target correspond à une des professions trouvées
  return matchingProfessions.some(
    profession =>
      profession.name.toLowerCase().includes(target.toLowerCase()) ||
      target.toLowerCase().includes(profession.name.toLowerCase())
  );
}

const SearchBar: React.FC<SearchBarProps> = ({
  onSearch,
  withMarginTop,
  showGuidance = true,
  initialProfession,
  initialAddress,
  embedded = false,
  idPrefix = 'search-bar',
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isFeatureEnabled } = useFeatureFlags();
  const geoCountry = useGeoCountry();
  const { t, i18n } = useTranslation('navigation');
  /** #1114 v8 : libellé d'un métier (slug) dans la langue courante. */
  const professionLabel = React.useCallback(
    (slug: string | null | undefined, fallback: string) =>
      slug ? t(`professions.${slug}`, { defaultValue: fallback }) : fallback,
    [t]
  );
  const { t: td } = useTranslation('discovery');
  const discoveryOn = isFeatureEnabled(FeatureFlag.PRO_DISCOVERY);
  const associationsOn = isFeatureEnabled(FeatureFlag.ASSOCIATION_PAGE);
  const askEnabled = isBookingAskAvailable({
    bookingAskEnabled: isFeatureEnabled(FeatureFlag.BOOKING_ASK),
    discoveryEnabled: discoveryOn,
    geoCountry,
    hasInitialProfession: Boolean(initialProfession),
  });
  const quoiId = `${idPrefix}-quoi`;
  const ouId = `${idPrefix}-ou`;
  const guidanceId = `${idPrefix}-guidance`;

  // ÉTATS
  const [searchQuoi, setSearchQuoi] = useState('');
  const [searchOu, setSearchOu] = useState('');
  const [suggestions, setSuggestions] = useState<AutocompleteItem[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [isSuggestionsLoading, setIsSuggestionsLoading] = useState(false);

  const [selectedProfessionSlug, setSelectedProfessionSlug] = useState<string | null>(null);
  const [selectedServiceTypeId, setSelectedServiceTypeId] = useState<number | null>(null);
  const [selectedServiceTypeName, setSelectedServiceTypeName] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties | undefined>();
  const [ouDropdownStyle, setOuDropdownStyle] = useState<React.CSSProperties | undefined>();
  const [showOuSuggestions, setShowOuSuggestions] = useState(false);
  const [ouPredictions, setOuPredictions] = useState<{ description: string; place_id: string }[]>(
    []
  );

  const suggestionRef = useRef<HTMLDivElement>(null);
  const quoiContainerRef = useRef<HTMLDivElement>(null);
  const searchBarRef = useRef<HTMLDivElement>(null);
  const quoiInputRef = useRef<HTMLInputElement>(null);
  const suggestionsDropdownRef = useRef<HTMLDivElement>(null);
  const ouContainerRef = useRef<HTMLDivElement>(null);
  const ouDropdownRef = useRef<HTMLDivElement>(null);
  const placesServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesSessionRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const ouCoordsRef = useRef<{ lat: number; lon: number } | null>(null);
  const nearMeGenRef = useRef(0);
  const selectedPlaceIdRef = useRef<string | null>(null);
  const pendingOuDetailsRef = useRef<Promise<{
    lat: number;
    lon: number;
    address: string;
  } | null> | null>(null);
  const placesAutocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const isMountedRef = useRef(true);
  const overlaySuggestions = embedded || isMobile;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const clearSelectedOuPlace = () => {
    ouCoordsRef.current = null;
    selectedPlaceIdRef.current = null;
    pendingOuDetailsRef.current = null;
  };

  // Utiliser le debouncing pour optimiser les appels API
  const debouncedSearchQuoi = useDebounce(searchQuoi, 300);
  const debouncedSearchOu = useDebounce(searchOu, 250);

  // Add these new states
  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
  const [isAutocompleteLoaded, setIsAutocompleteLoaded] = useState(false);
  const [mapsRequested, setMapsRequested] = useState(false);
  const requestMaps = React.useCallback(() => {
    setMapsRequested(true);
  }, []);

  const { isLoaded: isGoogleMapsLoaded } = useGoogleMaps();
  const mapsActiveForOu = mapsRequested && isGoogleMapsLoaded;

  useEffect(() => {
    const schedule =
      typeof window.requestIdleCallback === 'function'
        ? window.requestIdleCallback.bind(window)
        : (cb: () => void) => window.setTimeout(cb, 1500);
    const cancel =
      typeof window.cancelIdleCallback === 'function'
        ? window.cancelIdleCallback.bind(window)
        : (id: number) => window.clearTimeout(id);
    const id = schedule(() => requestMaps());
    return () => cancel(id as number);
  }, [requestMaps]);

  const overlayDropdownStyle = React.useCallback((anchor: HTMLElement | null) => {
    if (!anchor) return undefined;
    const rect = anchor.getBoundingClientRect();
    const viewport = window.visualViewport;
    const viewportHeight = viewport?.height ?? window.innerHeight;
    const viewportTop = viewport?.offsetTop ?? 0;
    const dropdownTop = rect.bottom + 8;
    const availableHeight = Math.max(120, viewportHeight - (dropdownTop - viewportTop) - 12);
    return {
      position: 'fixed' as const,
      top: dropdownTop,
      left: rect.left,
      width: rect.width,
      maxHeight: availableHeight,
      zIndex: SEARCH_SUGGESTIONS_Z_INDEX,
      WebkitOverflowScrolling: 'touch' as const,
    };
  }, []);

  useEffect(() => {
    if (!overlaySuggestions || !mapsActiveForOu || !window.google?.maps?.places) {
      placesServiceRef.current = null;
      return;
    }
    placesServiceRef.current = new window.google.maps.places.AutocompleteService();
    placesSessionRef.current = new window.google.maps.places.AutocompleteSessionToken();
  }, [overlaySuggestions, mapsActiveForOu]);

  useEffect(() => {
    if (!overlaySuggestions || !showOuSuggestions) {
      setOuPredictions([]);
      return undefined;
    }
    const value = debouncedSearchOu.trim();
    if (!placesServiceRef.current || value.length < 2) {
      setOuPredictions([]);
      return undefined;
    }
    let cancelled = false;
    placesServiceRef.current.getPlacePredictions(
      {
        input: value,
        sessionToken: placesSessionRef.current ?? undefined,
        componentRestrictions: {
          country: placesSearchCountryRestriction(geoCountry),
        },
        types: ['(regions)'],
      },
      (results, status) => {
        if (cancelled) return;
        if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
          setOuPredictions(
            results.slice(0, 6).map(r => ({
              description: r.description,
              place_id: r.place_id,
            }))
          );
        } else {
          setOuPredictions([]);
        }
      }
    );
    return () => {
      cancelled = true;
    };
  }, [debouncedSearchOu, geoCountry, mapsActiveForOu, overlaySuggestions, showOuSuggestions]);

  /** Reprendre filtres discovery depuis l’URL (query, address, legacy quoi/ou) */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const quoiParam = params.get('quoi');
    const ouParam = params.get('ou');
    const freeTextParam =
      params.get('ask') || params.get('seed') || params.get('query') || params.get('q');
    const addressParam = params.get('address');
    const professionParam = params.get('profession') ?? initialProfession ?? null;
    const serviceTypeParam = params.get('serviceType') || params.get('service');
    const serviceTypeIdParam = params.get('serviceTypeId') || params.get('service_type_id');

    if (professionParam) {
      setSelectedProfessionSlug(professionParam);
    } else {
      setSelectedProfessionSlug(null);
    }

    if (serviceTypeIdParam) {
      const parsedServiceTypeId = parseInt(serviceTypeIdParam, 10);
      setSelectedServiceTypeId(Number.isNaN(parsedServiceTypeId) ? null : parsedServiceTypeId);
      setSelectedServiceTypeName(serviceTypeParam);
    } else {
      setSelectedServiceTypeId(null);
      setSelectedServiceTypeName(null);
    }

    if (serviceTypeParam) {
      setSearchQuoi(serviceTypeParam);
    } else if (freeTextParam) {
      setSearchQuoi(freeTextParam);
    } else if (quoiParam) {
      setSearchQuoi(quoiParam);
    } else if (professionParam) {
      setSearchQuoi(professionLabel(professionParam, labelForProfessionSlug(professionParam) ?? professionParam));
    } else {
      setSearchQuoi('');
    }

    if (addressParam) {
      setSearchOu(addressParam);
    } else if (ouParam) {
      setSearchOu(ouParam);
    } else if (initialAddress) {
      setSearchOu(initialAddress);
    } else {
      setSearchOu('');
    }
  }, [location.search, initialProfession, initialAddress]);

  // #1114 v8 : métier sélectionné → libellé toujours dans la langue courante (changement de langue compris)
  useEffect(() => {
    if (!selectedProfessionSlug) return;
    setSearchQuoi(prev =>
      professionLabel(selectedProfessionSlug, labelForProfessionSlug(selectedProfessionSlug) ?? prev)
    );
  }, [selectedProfessionSlug, i18n.language, professionLabel]);

  /** Autocomplétion : quand l'utilisateur tape dans "Quoi" avec debouncing */
  useEffect(() => {
    if (debouncedSearchQuoi.trim().length > 0) {
      setIsSuggestionsLoading(true);
      fetchAutocomplete(debouncedSearchQuoi)
        .then(res => {
          setSuggestions(res);
        })
        .catch(error => {
          // TODO: Remplacer par un système de logging approprié
          // console.error("Erreur lors de l'autocomplétion:", error);
          setSuggestions([]);
        })
        .finally(() => {
          setIsSuggestionsLoading(false);
        });
    } else {
      setSuggestions([]);
      setIsSuggestionsLoading(false);
    }
  }, [debouncedSearchQuoi]);

  /** Nettoyer le cache expiré */
  useEffect(() => {
    // Nettoyer le cache expiré
    SimpleCache.cleanExpiredEntries();
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const updateDropdownPosition = React.useCallback(() => {
    if (!showSuggestions) {
      setDropdownStyle(undefined);
      return;
    }

    const anchor = (embedded && searchBarRef.current) || quoiContainerRef.current;
    setDropdownStyle(overlaySuggestions ? overlayDropdownStyle(anchor) : undefined);
  }, [embedded, overlayDropdownStyle, overlaySuggestions, showSuggestions]);

  const updateOuDropdownPosition = React.useCallback(() => {
    if (!showOuSuggestions || !overlaySuggestions) {
      setOuDropdownStyle(undefined);
      return;
    }
    setOuDropdownStyle(overlayDropdownStyle(ouContainerRef.current));
  }, [overlayDropdownStyle, overlaySuggestions, showOuSuggestions]);

  // #1114 v7 : le rAF de setSearchFocus doit appeler la version à jour (sinon closure figée avec
  // showSuggestions=false → style effacé → panneau portail sans top, rendu sous l'écran en 390)
  const updateDropdownPositionRef = useRef(updateDropdownPosition);
  updateDropdownPositionRef.current = updateDropdownPosition;

  useEffect(() => {
    updateDropdownPosition();
    if (!showSuggestions) return undefined;

    const viewport = window.visualViewport;
    viewport?.addEventListener('resize', updateDropdownPosition);
    viewport?.addEventListener('scroll', updateDropdownPosition);
    window.addEventListener('resize', updateDropdownPosition);
    window.addEventListener('scroll', updateDropdownPosition, true);

    return () => {
      viewport?.removeEventListener('resize', updateDropdownPosition);
      viewport?.removeEventListener('scroll', updateDropdownPosition);
      window.removeEventListener('resize', updateDropdownPosition);
      window.removeEventListener('scroll', updateDropdownPosition, true);
    };
  }, [showSuggestions, updateDropdownPosition]);

  useEffect(() => {
    updateOuDropdownPosition();
    if (!showOuSuggestions) return undefined;

    const viewport = window.visualViewport;
    viewport?.addEventListener('resize', updateOuDropdownPosition);
    viewport?.addEventListener('scroll', updateOuDropdownPosition);
    window.addEventListener('resize', updateOuDropdownPosition);
    window.addEventListener('scroll', updateOuDropdownPosition, true);

    return () => {
      viewport?.removeEventListener('resize', updateOuDropdownPosition);
      viewport?.removeEventListener('scroll', updateOuDropdownPosition);
      window.removeEventListener('resize', updateOuDropdownPosition);
      window.removeEventListener('scroll', updateOuDropdownPosition, true);
    };
  }, [showOuSuggestions, updateOuDropdownPosition]);

  const setSearchFocus = React.useCallback(
    (focused: boolean) => {
      setIsSearchFocused(focused);
      if (focused && isMobile) {
        requestAnimationFrame(() => {
          quoiInputRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
          updateDropdownPositionRef.current();
        });
      }
    },
    [isMobile]
  );

  /** Fermer la liste si clic hors zone */
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      const insideInput = suggestionRef.current?.contains(target);
      const insideDropdown = suggestionsDropdownRef.current?.contains(target);
      const insideOu = ouContainerRef.current?.contains(target);
      const insideOuDropdown = ouDropdownRef.current?.contains(target);
      if (!insideInput && !insideDropdown) {
        setShowSuggestions(false);
      }
      if (!insideOu && !insideOuDropdown) {
        setShowOuSuggestions(false);
      }
      if (!insideInput && !insideDropdown && !insideOu && !insideOuDropdown) {
        setSearchFocus(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setSearchFocus]);

  /** Quand l'utilisateur clique sur une suggestion */
  const handleSelectSuggestion = (item: AutocompleteItem) => {
    // On réinitialise le message d'erreur
    setErrorMessage(null);

    if (item.type === 'pro') {
      // Ex : redirection directe vers un profil
      navigate(`/pro/${item.slug}`);
    } else if (item.type === 'profession') {
      // On mémorise le slug de la profession
      setSelectedProfessionSlug(item.slug || null);
      setSelectedServiceTypeId(null);
      setSelectedServiceTypeName(null);
      // #1114 v8 : libellé dans la langue courante (professions.<slug>), pas le nom FR de la liste
      setSearchQuoi(professionLabel(item.slug, item.name));
    } else if (item.type === 'association') {
      if (!associationsOn) return;
      navigate(`/association/${item.slug}`);
    } else if (item.type === 'service') {
      setSearchQuoi(item.name);
      setSelectedProfessionSlug(null);
      setSelectedServiceTypeId(item.id);
      setSelectedServiceTypeName(item.name);
    }
    setShowSuggestions(false);
  };

  const handleSuggestionActivate = (item: AutocompleteItem) => (event: React.PointerEvent) => {
    event.preventDefault();
    handleSelectSuggestion(item);
  };

  // Préparer les éléments pour la navigation au clavier
  const getKeyboardNavigationItems = () => {
    let professionSuggestions = suggestions.filter(item => item.type === 'profession');
    const serviceSuggestions = suggestions.filter(item => item.type === 'service');
    const proSuggestions = suggestions.filter(item => item.type === 'pro');
    const associationSuggestions = suggestions.filter(item => item.type === 'association');

    if (searchQuoi.trim() && professionSuggestions.length === 0) {
      professionSuggestions = findMatchingProfessionsSimple(searchQuoi);
    }

    if (searchQuoi.trim() && suggestions.length > 0) {
      professionSuggestions = professionSuggestions.filter(item =>
        fuzzyMatch(searchQuoi, item.name)
      );
    }

    let filteredServices = serviceSuggestions;
    if (searchQuoi.trim() && suggestions.length > 0) {
      filteredServices = filteredServices.filter(item => fuzzyMatch(searchQuoi, item.name));
    }

    if (
      professionSuggestions.length === 0 &&
      filteredServices.length === 0 &&
      proSuggestions.length === 0 &&
      associationSuggestions.length === 0
    ) {
      return findMatchingProfessionsSimple('');
    }

    return [
      ...professionSuggestions,
      ...filteredServices,
      ...proSuggestions,
      ...associationSuggestions,
    ];
  };

  const allSuggestions = getKeyboardNavigationItems();

  // Navigation au clavier (simplifiée temporairement)
  const [selectedIndex, setSelectedIndex] = useState(-1);

  /** Remplit « Près de » avec la position du visiteur. La recherche part ensuite via Trouver. */
  const handleNearMe = async () => {
    if (isLocating || isLoading) return;
    if (!isMountedRef.current) return;
    const gen = ++nearMeGenRef.current;
    setErrorMessage(null);
    setShowSuggestions(false);
    setShowOuSuggestions(false);
    setIsLocating(true);
    try {
      const position = await getCurrentCoordinates();
      if (!isMountedRef.current || gen !== nearMeGenRef.current) return;
      const { latitude, longitude } = position;
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        setErrorMessage(t('mobileSearch.locationUnavailable'));
        return;
      }
      const label =
        (await reverseGeocodeCoords(latitude, longitude, i18n.language)) ??
        t('mobileSearch.nearMe');
      if (!isMountedRef.current || gen !== nearMeGenRef.current) return;
      ouCoordsRef.current = { lat: latitude, lon: longitude };
      selectedPlaceIdRef.current = null;
      pendingOuDetailsRef.current = null;
      setSearchOu(label);
    } catch (error) {
      if (!isMountedRef.current || gen !== nearMeGenRef.current) return;
      if (error instanceof GeolocationRequestError) {
        if (error.code === 'denied') {
          setErrorMessage(t('mobileSearch.locationDenied'));
        } else if (error.code === 'timeout') {
          setErrorMessage(t('mobileSearch.locationTimeout'));
        } else if (error.code === 'unavailable' || error.code === 'unsupported') {
          setErrorMessage(t('mobileSearch.locationUnavailable'));
        } else {
          setErrorMessage(t('mobileSearch.locationFailed'));
        }
      } else {
        setErrorMessage(t('mobileSearch.locationFailed'));
      }
    } finally {
      if (isMountedRef.current && gen === nearMeGenRef.current) {
        setIsLocating(false);
      }
    }
  };

  /** Lancer la recherche (ou le mode question). En embedded, toujours search classique. */
  const handleSearch = async () => {
    setErrorMessage(null);
    setIsLoading(true);

    try {
      if (!discoveryOn) {
        setErrorMessage(td('searchBar.errorUnavailable'));
        return;
      }

      // Dans la conversation, toujours search classique (pas d'ask inféré)
      const placeDetails = pendingOuDetailsRef.current ? await pendingOuDetailsRef.current : null;
      const ouCoords = ouCoordsRef.current;
      const result = await submitDiscoveryOrAsk({
        searchQuoi,
        searchOu: (placeDetails?.address || searchOu).trim(),
        selectedProfessionSlug,
        selectedServiceTypeId,
        selectedServiceTypeName,
        lat: ouCoords?.lat ?? 0,
        lon: ouCoords?.lon ?? 0,
        currentLocationSearch: location.search,
        country: geoCountry,
        askEnabled: embedded ? false : askEnabled,
      });

      if (result.status === 'need_location') {
        setErrorMessage(td('searchBar.errorLocationRequired'));
        return;
      }
      if (result.status === 'incomplete') {
        setErrorMessage(td('searchBar.errorIncomplete'));
        return;
      }

      navigate(result.path);
      onSearch?.();
    } catch {
      setErrorMessage(td('searchBar.errorSearchFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  // Add this new handler
  const onLoad = (autocomplete: google.maps.places.Autocomplete) => {
    placesAutocompleteRef.current = autocomplete;
    setAutocomplete(autocomplete);
    setIsAutocompleteLoaded(true);
  };

  const onPlaceChanged = () => {
    try {
      const ac = placesAutocompleteRef.current;
      if (!ac) return;
      const place = ac.getPlace();
      if (place.formatted_address) {
        setSearchOu(place.formatted_address);
      }
      ouCoordsRef.current = coordsFromGoogleLocation(place.geometry?.location);
      selectedPlaceIdRef.current = place.place_id || null;
      pendingOuDetailsRef.current = null;
    } catch {
      setErrorMessage(t('mobileSearch.locationFailed'));
      ouCoordsRef.current = null;
      selectedPlaceIdRef.current = null;
      pendingOuDetailsRef.current = null;
    }
  };

  // Add cleanup for Google Maps autocomplete
  useEffect(() => {
    return () => {
      // Cleanup pac-container elements when component unmounts
      const pacContainers = document.querySelectorAll('.pac-container');
      pacContainers.forEach(container => {
        container.remove();
      });
    };
  }, []);

  return (
    <div
      className={`search-bar-container ${withMarginTop ? 'with-margin' : ''} ${
        isSearchFocused ? 'search-bar-container--focused' : ''
      }${embedded ? ' search-bar-container--embedded' : ''}`}
    >
      {showGuidance && !embedded && (
        <p className='search-bar-guidance' id={guidanceId}>
          {td('searchBar.guidance')}
        </p>
      )}
      <div
        ref={searchBarRef}
        className={`search-bar${isSearchFocused ? ' search-bar--focused' : ''}`}
        role='search'
        aria-describedby={showGuidance && !embedded ? guidanceId : undefined}
      >
        <div
          className={`search-input-container suggestions-wrapper${
            showSuggestions && isMobile ? ' suggestions-wrapper--open' : ''
          }`}
          ref={el => {
            const node = el as HTMLDivElement | null;
            (suggestionRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
            (quoiContainerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
          }}
        >
          <label htmlFor={quoiId}>{t('mobileSearch.lookingFor')}</label>
          <input
            ref={quoiInputRef}
            id={quoiId}
            className='search-input'
            type='text'
            placeholder={t('mobileSearch.quoiPlaceholder')}
            autoComplete='off'
            value={searchQuoi}
            onChange={e => {
              setSearchQuoi(e.target.value);
              setShowSuggestions(true);
              setSelectedProfessionSlug(null);
              setSelectedServiceTypeId(null);
              setSelectedServiceTypeName(null);
            }}
            onFocus={() => {
              setShowSuggestions(true);
              setShowOuSuggestions(false);
              setSearchFocus(true);
            }}
            // #1114 v8 : un nouveau clic sur Service (déjà focalisé) rouvre le panneau
            onClick={() => setShowSuggestions(true)}
            onBlur={() => {
              window.setTimeout(() => setSearchFocus(false), 150);
            }}
            onKeyDown={e => {
              // #1114 v6 : Entrée lance la recherche (message « ville requise » / « recherche incomplète » au lieu de rien)
              if (e.key === 'Enter') {
                e.preventDefault();
                setShowSuggestions(false);
                void handleSearch();
              }
              // #1114 v8 : Escape ferme le panneau
              if (e.key === 'Escape') {
                setShowSuggestions(false);
              }
            }}
          />

          {showSuggestions &&
            (() => {
              const dropdown = (
                <div
                  ref={suggestionsDropdownRef}
                  className={`suggestions-dropdown${
                    overlaySuggestions ? ' suggestions-dropdown--portal' : ''
                  }`}
                  style={overlaySuggestions ? dropdownStyle : undefined}
                  onMouseDown={event => event.preventDefault()}
                >
                  {isSuggestionsLoading ? (
                    <div className='suggestions-loading'>
                      <CircularProgress size={20} />
                      <span>{td('searchBar.searching')}</span>
                    </div>
                  ) : (
                    (() => {
                      let professionSuggestions = suggestions.filter(
                        item => item.type === 'profession'
                      );
                      const serviceSuggestions = suggestions.filter(
                        item => item.type === 'service'
                      );
                      const proSuggestions = suggestions.filter(item => item.type === 'pro');
                      const associationSuggestions = suggestions.filter(
                        item => item.type === 'association'
                      );
                      const query = searchQuoi.trim();

                      if (query && professionSuggestions.length === 0) {
                        professionSuggestions = findMatchingProfessionsSimple(query);
                      }

                      if (query && suggestions.length > 0) {
                        professionSuggestions = professionSuggestions.filter(
                          item => fuzzyMatch(query, item.name) || smartMatch(query, item.name)
                        );
                      }

                      const filteredServices = query
                        ? serviceSuggestions.filter(
                            item =>
                              smartMatch(query, item.name) || fuzzyMatchSimple(query, item.name)
                          )
                        : serviceSuggestions;

                      const hasResults =
                        professionSuggestions.length > 0 ||
                        filteredServices.length > 0 ||
                        proSuggestions.length > 0 ||
                        associationSuggestions.length > 0;

                      const offsetService = proSuggestions.length;
                      const offsetProfession = offsetService + filteredServices.length;
                      const offsetAssoc = offsetProfession + professionSuggestions.length;

                      return hasResults ? (
                        <ul>
                          {proSuggestions.length > 0 && (
                            <li className='suggestion-category'>
                              <div className='suggestion-header'>{td('searchBar.prosHeader')}</div>
                              <ul className='suggestion-items'>
                                {proSuggestions.map((item, index) => (
                                  <li
                                    key={`pro-${item.id}`}
                                    className={`suggestion-row ${selectedIndex === index ? 'selected' : ''}`}
                                    onPointerDown={handleSuggestionActivate(item)}
                                  >
                                    <SuggestionAvatar src={item.icon} />
                                    <span className='suggestion-main'>
                                      <span className='suggestion-title'>{item.name}</span>
                                      <span className='suggestion-description'>
                                        {td('searchBar.goToProPage')}
                                      </span>
                                    </span>
                                    <span className='suggestion-badge'>{td('searchBar.proBadge')}</span>
                                  </li>
                                ))}
                              </ul>
                            </li>
                          )}

                          {filteredServices.length > 0 && (
                            <li className='suggestion-category'>
                              <div className='suggestion-header'>{td('searchBar.servicesHeader')}</div>
                              <ul className='suggestion-items'>
                                {filteredServices.map((item, index) => {
                                  const globalIndex = offsetService + index;
                                  return (
                                    <li
                                      key={`service-${item.id}`}
                                      className={`suggestion-row ${selectedIndex === globalIndex ? 'selected' : ''}`}
                                      onPointerDown={handleSuggestionActivate(item)}
                                    >
                                      <span className='suggestion-main'>
                                        <span className='suggestion-title'>{item.name}</span>
                                        <span className='suggestion-description'>
                                          {td('searchBar.findProsWithService')}
                                        </span>
                                      </span>
                                      <span className='suggestion-badge suggestion-badge--service'>
                                        {td('searchBar.serviceBadge')}
                                      </span>
                                    </li>
                                  );
                                })}
                              </ul>
                            </li>
                          )}

                          {professionSuggestions.length > 0 && (
                            <li className='suggestion-category'>
                              <div className='suggestion-header'>
                                {suggestions.length === 0 && query
                                  ? td('searchBar.suggestedProfessions')
                                  : td('searchBar.professions')}
                              </div>
                              <ul className='suggestion-items'>
                                {professionSuggestions.map((item, index) => {
                                  const isDefault = suggestions.length === 0 && query;
                                  const globalIndex = offsetProfession + index;
                                  return (
                                    <li
                                      key={`profession-${item.id}`}
                                      className={`suggestion-row ${selectedIndex === globalIndex ? 'selected' : ''}`}
                                      data-suggestion-type={isDefault ? 'default' : 'api'}
                                      onPointerDown={handleSuggestionActivate(item)}
                                    >
                                      <span className='suggestion-main'>
                                        <span className='suggestion-title'>{t(`professions.${item.slug}`, { defaultValue: item.name })}</span>
                                        <span className='suggestion-description'>
                                          {td('searchBar.browseProfession')}
                                        </span>
                                      </span>
                                      <span className='suggestion-badge suggestion-badge--muted'>
                                        {td('searchBar.professionBadge')}
                                      </span>
                                    </li>
                                  );
                                })}
                              </ul>
                            </li>
                          )}

                          {associationSuggestions.length > 0 && (
                            <li className='suggestion-category'>
                              <div className='suggestion-header'>{td('searchBar.associations')}</div>
                              <ul className='suggestion-items'>
                                {associationSuggestions.map((item, index) => {
                                  const globalIndex = offsetAssoc + index;
                                  return (
                                    <li
                                      key={`association-${item.id}`}
                                      className={`suggestion-row ${selectedIndex === globalIndex ? 'selected' : ''}`}
                                      onPointerDown={handleSuggestionActivate(item)}
                                    >
                                      <span className='suggestion-main'>
                                        <span className='suggestion-title'>{item.name}</span>
                                        <span className='suggestion-description'>
                                          {td('searchBar.viewAssociation')}
                                        </span>
                                      </span>
                                      <span className='suggestion-badge'>
                                        {td('searchBar.associationBadge')}
                                      </span>
                                    </li>
                                  );
                                })}
                              </ul>
                            </li>
                          )}
                        </ul>
                      ) : (
                        <ul>
                          <li className='suggestion-category'>
                            <div className='suggestion-header'>
                              {td('searchBar.popularProfessions')}
                            </div>
                            <ul className='suggestion-items'>
                              {findMatchingProfessionsSimple('').map((item: any, index: number) => (
                                <li
                                  key={`default-${item.id}`}
                                  className={`suggestion-row ${selectedIndex === index ? 'selected' : ''}`}
                                  data-suggestion-type='default'
                                  onPointerDown={handleSuggestionActivate(item)}
                                >
                                  <span className='suggestion-main'>
                                    <span className='suggestion-title'>
                                      {professionLabel(String(item.slug), item.name)}
                                    </span>
                                  </span>
                                  <span className='suggestion-badge suggestion-badge--muted'>
                                    {td('searchBar.professionBadge')}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </li>
                        </ul>
                      );
                    })()
                  )}
                </div>
              );

              return overlaySuggestions ? createPortal(dropdown, document.body) : dropdown;
            })()}
        </div>

        <div
          className={`separator${showSuggestions && isMobile ? ' search-bar__hidden-while-suggesting' : ''}`}
        />

        <div
          ref={ouContainerRef}
          className={`search-input-container${
            showSuggestions && isMobile ? ' search-bar__hidden-while-suggesting' : ''
          }`}
          onPointerEnter={requestMaps}
          onPointerDown={requestMaps}
        >
          <div className='search-field-label-row'>
            <label htmlFor={ouId}>{t('mobileSearch.near')}</label>
            <button
              type='button'
              className='search-near-me'
              onClick={() => void handleNearMe()}
              disabled={isLocating || isLoading}
            >
              <LocateFixed size={13} aria-hidden='true' />
              <span>{isLocating ? t('mobileSearch.locating') : t('mobileSearch.nearMe')}</span>
            </button>
          </div>
          <SearchBarOuField
            autocompleteKey={`searchbar-ou-${geoCountry}-${idPrefix}`}
            geoCountry={geoCountry}
            inputId={ouId}
            value={searchOu}
            placeholder={t('mobileSearch.ouPlaceholder')}
            mapsRequested={mapsRequested}
            onLoad={onLoad}
            onPlaceChanged={onPlaceChanged}
            onValueChange={setSearchOu}
            onClearSelectedPlace={clearSelectedOuPlace}
            onRequestMaps={requestMaps}
            onFocus={() => {
              setShowSuggestions(false);
              setSearchFocus(true);
            }}
            onBlur={() => {
              window.setTimeout(() => setSearchFocus(false), 150);
            }}
          />
        </div>

        {overlaySuggestions && showOuSuggestions && ouPredictions.length > 0
          ? createPortal(
              <div
                ref={ouDropdownRef}
                className='suggestions-dropdown suggestions-dropdown--portal'
                style={ouDropdownStyle}
                onMouseDown={event => event.preventDefault()}
              >
                <ul>
                  <li className='suggestion-category'>
                    <div className='suggestion-header'>{td('searchBar.placesHeader')}</div>
                    <ul className='suggestion-items'>
                      {ouPredictions.map(item => (
                        <li
                          key={item.place_id}
                          className='suggestion-row'
                          onPointerDown={event => {
                            event.preventDefault();
                            setSearchOu(item.description);
                            setShowOuSuggestions(false);
                            selectedPlaceIdRef.current = item.place_id;
                            ouCoordsRef.current = null;
                            const token = placesSessionRef.current;
                            pendingOuDetailsRef.current = resolveGooglePlaceCoords(
                              item.place_id,
                              token
                            ).then(details => {
                              if (selectedPlaceIdRef.current !== item.place_id) return null;
                              if (details) {
                                ouCoordsRef.current = { lat: details.lat, lon: details.lon };
                                if (details.address) setSearchOu(details.address);
                              }
                              if (window.google?.maps?.places) {
                                placesSessionRef.current =
                                  new window.google.maps.places.AutocompleteSessionToken();
                              }
                              return details;
                            });
                          }}
                        >
                          <span className='suggestion-main'>
                            <span className='suggestion-title'>{item.description}</span>
                          </span>
                          <span className='suggestion-badge suggestion-badge--muted'>Ville</span>
                        </li>
                      ))}
                    </ul>
                  </li>
                </ul>
              </div>,
              document.body
            )
          : null}

        <div
          className={`separator${showSuggestions && isMobile ? ' search-bar__hidden-while-suggesting' : ''}`}
        />

        <button
          type='button'
          className={`search-btn${showSuggestions && isMobile ? ' search-bar__hidden-while-suggesting' : ''}`}
          onClick={() => void handleSearch()}
          disabled={isLoading}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '120px',
          }}
        >
          {isLoading ? (
            <CircularProgress size={24} color='inherit' />
          ) : (
            t('mobileSearch.findButton')
          )}
        </button>
      </div>

      {/* Affichage d'un éventuel message d'erreur */}
      {errorMessage && <div className='search-error-message'>{errorMessage}</div>}
    </div>
  );
};

export default SearchBar;
